import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { Wrench, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { CATEGORIES, ACTIONS } from './action-registry';
import type { ActionExecution } from './types';
import { ActionCard } from './components/ActionCard';
import { ActionHistory, loadHistory, saveHistory } from './components/ActionHistory';
import { HealthChecksPanel } from './components/HealthChecksPanel';
import { AccessMigrationPanel } from './components/AccessMigrationPanel';

interface StoreListItem {
  id: number;
  name: string;
  integrations: string[];
}

export function OperationsPage() {
  const [history, setHistory] = useState<ActionExecution[]>(loadHistory);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const { data: stores } = useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: async ({ signal }) => {
      const res = await api.get<StoreListItem[]>('/api/v1/admin/stores', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed');
      return res.data;
    },
  });

  const storeOptions = (stores ?? []).map((s) => ({ id: s.id, name: s.name, integrations: s.integrations }));

  const handleExecuted = useCallback((execution: ActionExecution) => {
    setHistory((prev) => {
      const next = [execution, ...prev].slice(0, 50);
      saveHistory(next);
      return next;
    });
  }, []);

  const handleToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
  }, []);

  const handleClearHistory = useCallback(() => {
    setHistory([]);
    saveHistory([]);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const actionsByCategory = CATEGORIES.map((cat) => ({
    ...cat,
    actions: ACTIONS.filter((a) => a.category === cat.id),
  })).filter((cat) => cat.actions.length > 0);

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {toast.message}
          <button onClick={() => setToast(null)} className="ml-2 opacity-70 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2">
            <Wrench className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Operations Center</h2>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Quick fixes, diagnostics, and maintenance actions</p>
        </div>
        <div className="text-[10px] text-gray-400 dark:text-gray-500">
          {ACTIONS.length} actions available
        </div>
      </div>

      {/* Health Checks */}
      <div className="mb-6">
        <HealthChecksPanel stores={storeOptions} />
      </div>

      {/* Access Migration Controls */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Access Migration</span>
          <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
        </div>
        <AccessMigrationPanel />
      </div>

      {/* Action Categories */}
      <div className="space-y-6 mb-6">
        {actionsByCategory.map((cat) => {
          const CatIcon = cat.icon;
          return (
            <div key={cat.id}>
              <div className="flex items-center gap-2 mb-2">
                <CatIcon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{cat.label}</span>
                <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {cat.actions.map((action) => (
                  <ActionCard
                    key={action.id}
                    action={action}
                    stores={storeOptions}
                    onExecuted={handleExecuted}
                    onToast={handleToast}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* History */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Recent Operations</span>
          <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
        </div>
        <ActionHistory history={history} onClear={handleClearHistory} />
      </div>
    </div>
  );
}
